import React from 'react';
import { TestTube, CheckCircle, AlertCircle, Eye, EyeOff, Save } from 'lucide-react';
import type { BudgetConfig, BudgetTestResult } from '../types';

interface BudgetSettingsTabProps {
  config: BudgetConfig;
  onChange: (config: BudgetConfig) => void;
  onTest: () => void;
  onSave: () => void;
  testingConnection: boolean;
  savingConfig: boolean;
  testResult: BudgetTestResult | null;
  showApiKey: boolean;
  onToggleApiKey: () => void;
}

const BudgetSettingsTab: React.FC<BudgetSettingsTabProps> = ({
  config,
  onChange,
  onTest,
  onSave,
  testingConnection,
  savingConfig,
  testResult,
  showApiKey,
  onToggleApiKey
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Configuración del Sistema de Presupuestos</h2>
        <p className="text-sm text-gray-600 mb-6">
          Configura la integración con el sistema externo de presupuestos para sincronizar propuestas,
          tareas y milestones automáticamente.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Configuración en Tiempo de Ejecución</p>
            <p>
              Esta configuración se guarda en la base de datos y se puede actualizar sin reiniciar
              el servidor. Las variables de entorno tienen prioridad sobre esta configuración.
            </p>
          </div>
        </div>
      </div>

      {config.isActive && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-900">Configuración Activa</p>
              {config.lastTestedAt && (
                <p className="text-xs text-green-700 mt-1">
                  Última prueba: {new Date(config.lastTestedAt).toLocaleString('es-ES')}
                  {config.testMessage && ` - ${config.testMessage}`}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            URL del Sistema de Presupuestos
          </label>
          <input
            type="url"
            value={config.apiUrl}
            onChange={(e) => onChange({ ...config, apiUrl: e.target.value })}
            placeholder="https://xyevupkaytokqdvbgbwb.supabase.co"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500">
            La URL base de Supabase del sistema de presupuestos
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Service Role Key</label>
          <div className="relative">
            <input
              type={showApiKey ? 'text' : 'password'}
              value={config.apiKey}
              onChange={(e) => onChange({ ...config, apiKey: e.target.value })}
              placeholder={config.isActive ? '••••••••••••••••' : 'Pega aquí el Service Role Key'}
              className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              type="button"
              onClick={onToggleApiKey}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showApiKey ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            El Service Role Key del sistema de presupuestos (se guarda de forma segura)
          </p>
        </div>

        {testResult && (
          <div className={`p-4 rounded-lg border ${
            testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start space-x-2">
              {testResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p className={`font-semibold ${testResult.success ? 'text-green-900' : 'text-red-900'}`}>
                  {testResult.success ? 'Prueba Exitosa' : 'Prueba Fallida'}
                </p>
                <p className={`text-sm mt-1 ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {testResult.message}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-gray-200 flex justify-between space-x-3">
        <button
          onClick={onTest}
          disabled={testingConnection || !config.apiUrl || !config.apiKey}
          className="flex items-center space-x-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <TestTube className={`w-4 h-4 ${testingConnection ? 'animate-pulse' : ''}`} />
          <span>{testingConnection ? 'Probando...' : 'Probar Conexión'}</span>
        </button>

        <button
          onClick={onSave}
          disabled={savingConfig || !config.apiUrl || !config.apiKey}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Save className="w-4 h-4" />
          <span>{savingConfig ? 'Guardando...' : 'Guardar Configuración'}</span>
        </button>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Qué se sincroniza:</h3>
        <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
          <li>Propuestas aprobadas pendientes de importar</li>
          <li>Tareas y subtareas asociadas a cada propuesta</li>
          <li>Milestones con fechas estimadas de entrega</li>
          <li>Asignaciones de colaboradores a proyectos</li>
          <li>Presupuestos y estimaciones de tiempo</li>
        </ul>
        <p className="mt-3 text-xs text-gray-600">
          La sincronización se ejecuta automáticamente cada 5 minutos cuando esta configuración está activa.
        </p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-yellow-800">
            <p className="font-medium mb-1">Advertencia de Seguridad</p>
            <p>
              El Service Role Key tiene permisos completos sobre la base de datos. No lo compartas
              y asegúrate de que solo usuarios administradores tengan acceso a esta configuración.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetSettingsTab;
